import createReducer from 'redux/lib/reducerConfig';
import { reportError } from 'utils/reporting';
import { updateUserSettings } from 'redux/user';
import {
  selectedSortedSurveyJobProfileOptions,
  selectSurveyJobsFilter,
} from '../selectors';
import { SurveyJobSearchField, MappingService, SurveyLevelCompanyJobGlobalGradeMappingService, SurveyJobProfileService, SurveyReplacementService, SurveyService } from 'types'
import { runCancelableQuery } from 'utils/runCancelableQuery';
import { VendorLevelProfileType } from 'types';
import { GLOBAL_LEVEL_NOT_APPLICABLE } from 'common/constants';

export const filterInitialState = {
  jobFamily: [],
  jobSubFamily: [],
  jobFocus: [],
  careerLevel: [],
  globalLevel: [],
  search: [],
  searchFields: Object.values(SurveyJobSearchField),
  surveyJobProfileIds: [],
  module: [],
  jobType: null,
  purchasedOnly: true,
  sort: { jobLevel: 'ASC' },
};

const initialState = {
  jobs: [],
  jobsCount: 0,
  jobFamilies: [],
  careerLevels: [],
  globalLevels: [],
  mappedLevels: [],
  mappedLevelsGlobalGrade: [],
  filter: filterInitialState,
  isSurveyJobsLoading: false,
  surveyJobProfiles: [],
  surveyModules: [],
  activeSurveyJobProfileId: null,
  activeSurveyModuleId: [],
};

const ActionTypes = {
  SET_FILTER: 'MAPPING_JOB_SURVEY/SET_FILTER',
  RESET_FILTER: 'MAPPING_JOB_SURVEY/RESET_FILTER',
  SET_SURVEY_JOB_PROFILES: 'MAPPING_JOB_SURVEY/SET_SURVEY_JOB_PROFILES',
  SET_ACTIVE_SURVEY_JOB_PROFILE_ID: 'MAPPING_JOB_SURVEY/SET_ACTIVE_SURVEY_JOB_PROFILE_ID',
  SET_SURVEY_JOBS: 'MAPPING_JOB_SURVEY/SET_SURVEY_JOBS',
  SET_SURVEY_JOBS_LOADING: 'MAPPING_JOB_SURVEY/SET_SURVEY_JOBS_LOADING',
  SET_SURVEY_JOB_FAMILIES: 'MAPPING_JOB_SURVEY/SET_SURVEY_JOB_FAMILIES',
  SET_CAREER_LEVELS: 'MAPPING_JOB_SURVEY/SET_CAREER_LEVELS',
  SET_GLOBAL_LEVELS: 'MAPPING_JOB_SURVEY/SET_GLOBAL_LEVELS',
  SET_SURVEY_MAPPED_LEVELS: 'MAPPING_JOB_SURVEY/SET_SURVEY_MAPPED_LEVELS',
  SET_SURVEY_MAPPED_LEVELS_GLOBAL_GRADE: 'MAPPING_JOB_SURVEY/SET_SURVEY_MAPPED_LEVELS_GLOBAL_GRADE',
  SET_SURVEY_JOB_SUB_PROFILES: 'MAPPING_JOB_SURVEY/SET_SURVEY_JOB_SUB_PROFILES',
  SET_ACTIVE_SURVEY_JOB_SUB_PROFILE_ID: 'MAPPING_JOB_SURVEY/SET_ACTIVE_SURVEY_JOB_SUB_PROFILE_ID',
};

export const setFilter = (filter) => ({
  type: ActionTypes.SET_FILTER,
  payload: filter,
});

export const resetFilter = () => ({
  type: ActionTypes.RESET_FILTER,
});

function setSurveyJobProfiles(surveyJobProfiles) {
  return {
    type: ActionTypes.SET_SURVEY_JOB_PROFILES,
    payload: surveyJobProfiles,
  };
}
export function setActiveSurveyJobProfileId(surveyJobProfileId) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.SET_ACTIVE_SURVEY_JOB_PROFILE_ID,
      payload: surveyJobProfileId,
    });
  };
}

function setSurveyModules(surveyModules) {
  return {
    type: ActionTypes.SET_SURVEY_JOB_SUB_PROFILES,
    payload: surveyModules,
  };
}


export function setActiveSurveyModuleId(surveyModuleId) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.SET_ACTIVE_SURVEY_JOB_SUB_PROFILE_ID,
      payload: surveyModuleId,
    });
  };
}

export const loadSurveyJobProfiles = (plan, { purchased } = {}) => async (dispatch) => {
  try {
    const { rows: surveyJobProfiles } = await SurveyJobProfileService.listProfiles({ plan: [plan], purchased });
    dispatch(setSurveyJobProfiles(surveyJobProfiles));
    return surveyJobProfiles;
  } catch(err) {
    reportError(err);
    return [];
  }
};

export const loadSurveyModules = (surveyJobProfileIds) => async (dispatch) => {
  try {
    const surveyModules = surveyJobProfileIds.length ? (await SurveyService.listSurveyModules({ surveyJobProfileIds })).rows : [];
    dispatch(setSurveyModules(surveyModules));
    return surveyModules;
  } catch(err) {
    reportError(err);
    return [];
  }
};

export const loadSurveyJobFamilies = (surveyJobProfileIds) => async (dispatch) => {
  try {
    const jobFamilies = surveyJobProfileIds.length ? (await SurveyService.listFamilies({ surveyJobProfileIds })).rows : [];
    dispatch(setSurveyJobFamilies(jobFamilies));
  } catch (err) {
    reportError(err);
  }
};

export const loadSurveyJobProfileFilters = ({
  surveyJobProfileIds,
  globalVendorLevelProfileIds,
  careerVendorLevelProfileIds,
}) => async (dispatch) => {
  try {
    dispatch(loadSurveyJobFamilies(surveyJobProfileIds));
    dispatch(loadCareerVendorProfileLevelsFilters(careerVendorLevelProfileIds));
    dispatch(loadGlobalVendorProfileLevelsFilters(globalVendorLevelProfileIds));
    dispatch(loadSurveyModules(surveyJobProfileIds));
  } catch (err) {
    reportError(err);
  }
};

export const loadCareerVendorProfileLevelsFilters = (careerVendorLevelProfileIds) => async (dispatch) => {
  try {
    const jobLevels = careerVendorLevelProfileIds.length ? (await SurveyService.listSurveyLevels({
      vendorLevelProfileIds: careerVendorLevelProfileIds,
      type: VendorLevelProfileType.CAREER,
    })).rows : [];
    dispatch(setCareerLevels(jobLevels));
  } catch (err) {
    reportError(err);
  }
};

export const loadGlobalVendorProfileLevelsFilters = (globalVendorLevelProfileIds) => async (dispatch) => {
  try {
    const jobLevels = globalVendorLevelProfileIds.length ? (await SurveyService.listSurveyLevels({
      vendorLevelProfileIds: globalVendorLevelProfileIds,
      type: VendorLevelProfileType.GLOBAL,
    })).rows : [];
    dispatch(setGlobalLevels(jobLevels));
  } catch (err) {
    reportError(err);
  }
};

export const loadMappedSurveyLevels = ({
  companyJobLevelIds,
  careerVendorLevelProfileIds,
  planId
}) => async (dispatch) => {
  try {
    if (careerVendorLevelProfileIds.length) {
      const links = await MappingService.getJobLevelMappings({
        requestBody: {
          companyJobLevelIds,
          vendorLevelProfileIds: careerVendorLevelProfileIds,
          planId,
        }
      });
      const levels = links.map(level => level.surveyJobLevelId);
      dispatch(setMappedSurveyLevels(levels));
      return levels;
    } else {
      dispatch(setMappedSurveyLevels([]));
      return [];
    }
  } catch (err) {
    reportError(err);
  }
};

export const loadMappedSurveyLevelsGlobalGrade = ({
  companyJobGlobalGradeIds,
  globalVendorLevelProfileIds,
  planId,
  hasGlobalLevelsMapped,
}) => async (dispatch) => {
  if (!hasGlobalLevelsMapped) {
    return [GLOBAL_LEVEL_NOT_APPLICABLE];
  }

  try {
    if (globalVendorLevelProfileIds?.length && companyJobGlobalGradeIds?.length) {
      const links = await SurveyLevelCompanyJobGlobalGradeMappingService.getJobGlobalGradeSurveyLevelMappings({
        requestBody: {
          companyJobGlobalGradeIds,
          vendorLevelProfileIds: globalVendorLevelProfileIds,
          planId,
        }
      });
      const levels = links.map(level => level.surveyJobLevelId);
      dispatch(setMappedSurveyLevelsGlobalGrade(levels));
      return levels;
    } else {
      dispatch(setMappedSurveyLevelsGlobalGrade([]));
      return [];
    }
  } catch (err) {
    reportError(err);
  }
};

export const updateSurveyJobProfileSortOrder = (oldId, newId) => async (dispatch, getState) => {
  const sortedSurveyJobProfileOptions = selectedSortedSurveyJobProfileOptions(getState());

  dispatch(updateUserSettings({
    massJobMappingConfig: sortedSurveyJobProfileOptions.map(({ value: id }) => {
      if (id === oldId) {
        return newId;
      }
      if (id === newId) {
        return oldId;
      }
      return id;
    }),
  }));
};

export const loadSurveyJobs = (params) => async (dispatch, getState) => {
  dispatch(setSurveyJobsLoading(true));

  try {
    const { globalLevel, ...filter } = selectSurveyJobsFilter(getState());

    const response = await runCancelableQuery('fetchSurveyJobProfileJobs', () => SurveyService.listJobs({
      requestBody: {
        ...filter,
        ...params,
        globalLevel: globalLevel.length && globalLevel[0] === GLOBAL_LEVEL_NOT_APPLICABLE ? null : globalLevel,
      },
    }));

    if (response) {
      const { count, rows: surveyJobs } = response;
      dispatch(setSurveyJobs(surveyJobs, count));
    }
  } catch (err) {
    reportError(err);
  }

  dispatch(setSurveyJobsLoading(false));
};

export const loadSurveyJobsBySurveyId = ({ surveyId, ...params }) => async (dispatch, getState) => {
  dispatch(setSurveyJobsLoading(true));

  try {
    const { globalLevel, search, searchFields: _searchFields, surveyJobProfileIds: _surveyJobProfileIds, ...filter } = selectSurveyJobsFilter(getState());

    const response = await runCancelableQuery('fetchSurveyJobProfileJobs', () => SurveyReplacementService.searchSurveyJobs({
      requestBody: {
        ...filter,
        ...params,
        surveyId,
        search: search[0],
        globalLevel: globalLevel.length && globalLevel[0] === GLOBAL_LEVEL_NOT_APPLICABLE ? null : globalLevel,
      },
    }));

    if (response) {
      dispatch(setSurveyJobs(response.rows, undefined));
    }
  } catch (err) {
    reportError(err);
  }

  dispatch(setSurveyJobsLoading(false));
};

export const resetSurveyJobs = () => (dispatch) => {
  dispatch(setSurveyJobs([], 0));
};

function setSurveyJobs(jobs, count) {
  return {
    type: ActionTypes.SET_SURVEY_JOBS,
    payload: { jobs, count },
  };
}
function setSurveyJobsLoading(isSurveyJobsLoading) {
  return {
    type: ActionTypes.SET_SURVEY_JOBS_LOADING,
    payload: {
      isSurveyJobsLoading,
    },
  };
}

function setSurveyJobFamilies(jobFamilies) {
  return {
    type: ActionTypes.SET_SURVEY_JOB_FAMILIES,
    payload: jobFamilies,
  };
}

function setCareerLevels(careerLevels) {
  return {
    type: ActionTypes.SET_CAREER_LEVELS,
    payload: careerLevels,
  };
}

function setGlobalLevels(globalLevels) {
  return {
    type: ActionTypes.SET_GLOBAL_LEVELS,
    payload: globalLevels,
  };
}

function setMappedSurveyLevels(jobLevels) {
  return {
    type: ActionTypes.SET_SURVEY_MAPPED_LEVELS,
    payload: jobLevels,
  };
}

function setMappedSurveyLevelsGlobalGrade(jobLevels) {
  return {
    type: ActionTypes.SET_SURVEY_MAPPED_LEVELS_GLOBAL_GRADE,
    payload: jobLevels,
  };
}

export const surveyReducer = createReducer(
  initialState,
  {
    [ActionTypes.SET_FILTER]: (state, { payload }) => {
      return {
        ...state,
        filter: {
          ...state.filter,
          ...payload,
        },
      };
    },
    [ActionTypes.RESET_FILTER]: (state) => {
      return {
        ...state,
        filter: {
          ...filterInitialState,
          surveyJobProfileIds: state.filter.surveyJobProfileIds,
        },
      };
    },
    [ActionTypes.SET_SURVEY_JOB_PROFILES]: (state, action) => {
      return {
        ...state,
        surveyJobProfiles: action.payload,
      };
    },
    [ActionTypes.SET_ACTIVE_SURVEY_JOB_PROFILE_ID]: (state, action) => {
      return {
        ...state,
        activeSurveyJobProfileId: action.payload,
        ...(
          state.activeSurveyModuleId.length
            ? { activeSurveyModuleId: [] }
            : {}
        ),
      };
    },
    [ActionTypes.SET_SURVEY_JOBS]: (state, action) => {
      return {
        ...state,
        jobs: action.payload.jobs,
        jobsCount: action.payload.count,
      };
    },
    [ActionTypes.SET_SURVEY_JOBS_LOADING]: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    [ActionTypes.SET_SURVEY_JOB_FAMILIES]: (state, action) => {
      return {
        ...state,
        jobFamilies: action.payload,
      };
    },
    [ActionTypes.SET_CAREER_LEVELS]: (state, action) => {
      return {
        ...state,
        careerLevels: action.payload,
      };
    },
    [ActionTypes.SET_GLOBAL_LEVELS]: (state, action) => {
      return {
        ...state,
        globalLevels: action.payload,
      };
    },
    [ActionTypes.SET_SURVEY_MAPPED_LEVELS]: (state, action) => {
      return {
        ...state,
        mappedLevels: action.payload,
      };
    },
    [ActionTypes.SET_SURVEY_MAPPED_LEVELS_GLOBAL_GRADE]: (state, action) => {
      return {
        ...state,
        mappedLevelsGlobalGrade: action.payload,
      };
    },
    [ActionTypes.SET_SURVEY_JOB_SUB_PROFILES]: (state, action) => {
      return {
        ...state,
        surveyModules: action.payload,
      };
    },
    [ActionTypes.SET_ACTIVE_SURVEY_JOB_SUB_PROFILE_ID]: (state, action) => {
      return {
        ...state,
        activeSurveyModuleId: action.payload,
      };
    },
  }
);
